import { getFromLocalStorageAndDecrypt } from '../Components/WebChat/WebChatEncryptDecrypt';

const DEFAULT_TIMEOUT_MS = 10000;

/**
 * Error thrown by every request in this layer.
 *
 * Services return data or throw - they never toast and never dispatch, so the
 * caller decides how (and whether) to surface a failure. `status` is 0 for
 * timeouts and network faults, which never reached the server.
 */
export class HttpError extends Error {
    constructor(message, { status = 0, payload = null } = {}) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
        this.payload = payload;
    }
}

/**
 * Auth headers this backend expects. The gateway derives the user domain from the Host.
 *
 * @returns {object} Header map for an authenticated request.
 * @throws {HttpError} When the stored credentials are missing.
 */
const authHeaders = () => {
    const authUser = getFromLocalStorageAndDecrypt('auth_user');
    const token = getFromLocalStorageAndDecrypt('token');

    if (!authUser?.username) {
        throw new HttpError('User authentication is missing');
    }
    if (!token) {
        throw new HttpError('Authorization token is missing');
    }

    return {
        username: String(authUser.username),
        Authorization: token,
    };
};

/**
 * Send a request and unwrap the API envelope.
 *
 * The backend reports success as either `status` or `statusCode` 200 and nests
 * the payload under `data` only sometimes, so both shapes are normalised here
 * rather than at each call site.
 *
 * @param {string} url Absolute request URL.
 * @param {object} [options] Request options.
 * @param {string} [options.method] HTTP method.
 * @param {object} [options.body] JSON-serialisable request body.
 * @param {boolean} [options.auth] Attach auth headers. Leave false for
 *   pre-signed URLs, where an Authorization header invalidates the signature.
 * @param {number} [options.timeout] Abort after this many ms.
 * @param {object} [options.headers] Extra headers.
 * @param {AbortSignal} [options.signal] Caller signal, e.g. to cancel on unmount.
 * @returns {Promise<any>} The unwrapped payload.
 * @throws {HttpError} On timeout, network failure, or a non-200 envelope.
 */
export const request = async (url, options = {}) => {
    const { method = 'GET', body, auth = false, timeout = DEFAULT_TIMEOUT_MS, headers = {}, signal } = options;

    const controller = new AbortController();
    const onAbort = () => controller.abort();
    if (signal) {
        if (signal.aborted) controller.abort();
        else signal.addEventListener('abort', onAbort);
    }
    const timer = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(url, {
            method,
            headers: {
                Accept: '*/*',
                ...(body !== undefined && { 'Content-Type': 'application/json' }),
                ...(auth && authHeaders()),
                ...headers,
            },
            ...(body !== undefined && { body: JSON.stringify(body) }),
            signal: controller.signal,
        });

        // A failing response often still carries a useful message in its body.
        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new HttpError(payload?.message || `Request failed (${response.status})`, {
                status: response.status,
                payload,
            });
        }

        if (payload?.status !== 200 && payload?.statusCode !== 200) {
            throw new HttpError(payload?.message || 'Request failed', {
                status: response.status,
                payload,
            });
        }

        return payload.data ?? payload;
    } catch (error) {
        if (error instanceof HttpError) throw error;
        if (error?.name === 'AbortError') {
            throw new HttpError('Request timed out. Please try again.');
        }
        throw new HttpError('Network error occurred. Please try again later.');
    } finally {
        clearTimeout(timer);
        if (signal) signal.removeEventListener('abort', onAbort);
    }
};

export const get = (url, options) => request(url, { ...options, method: 'GET' });

export const post = (url, body, options) => request(url, { ...options, method: 'POST', body });
